/**
 * User API endpoints
 */
import axiosClient from './axiosClient';
import {
  User, UserProfile, CreateUserData, UpdateUserData, UserPasswordChange
} from '@/types';

// TTL cache + in-flight dedup for getUser. TaskCard calls this per task to
// resolve assignee names that aren't already in the project members list,
// so without dedup a Tasks page with many tasks produced one HTTP call per
// distinct assignee — most of them duplicates of each other.
const USER_TTL_MS = 5 * 60_000;
const userCache = new Map<string, { ts: number; data: UserProfile }>();
const inFlightUser = new Map<string, Promise<UserProfile>>();

export const userAPI = {
  /**
   * Get all users
   */
  getUsers: async (params?: { 
    organization_id?: string;
    is_active?: boolean;
    skip?: number; 
    limit?: number;
  }): Promise<User[]> => {
    const response = await axiosClient.get<User[]>('/users', { params });
    return response.data;
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async (): Promise<UserProfile> => {
    const response = await axiosClient.get<UserProfile>('/users/me');
    return response.data;
  },

  /**
   * Get user by ID
   */
  getUser: async (id: string): Promise<UserProfile> => {
    const cached = userCache.get(id);
    if (cached && Date.now() - cached.ts < USER_TTL_MS) return cached.data;

    const existing = inFlightUser.get(id);
    if (existing) return existing;

    const promise = (async () => {
      try {
        const response = await axiosClient.get<UserProfile>(`/users/${id}`);
        userCache.set(id, { ts: Date.now(), data: response.data });
        return response.data;
      } finally {
        inFlightUser.delete(id);
      }
    })();
    inFlightUser.set(id, promise);
    return promise;
  },

  /** Invalidate the in-memory user cache (call after profile mutations). */
  invalidateUserCache: (id?: string) => {
    if (!id) userCache.clear();
    else userCache.delete(id);
  },

  /**
   * Create new user
   */
  createUser: async (data: CreateUserData): Promise<User> => {
    const response = await axiosClient.post<User>('/users', data);
    return response.data;
  },

  /**
   * Update current user profile
   */
  updateCurrentUser: async (data: UpdateUserData): Promise<UserProfile> => {
    const response = await axiosClient.put<UserProfile>('/users/me', data);
    return response.data;
  },

  /**
   * Update user by ID
   */
  updateUser: async (id: string, data: UpdateUserData): Promise<UserProfile> => {
    const response = await axiosClient.put<UserProfile>(`/users/${id}`, data);
    return response.data;
  },

  /**
   * Delete user
   */
  deleteUser: async (id: string): Promise<void> => {
    await axiosClient.delete(`/users/${id}`);
  },

  /**
   * Verify user account
   */
  verifyUser: async (id: string): Promise<UserProfile> => {
    const response = await axiosClient.post<UserProfile>(`/users/${id}/verify`);
    return response.data;
  },

  /**
   * Deactivate user account
   */
  deactivateUser: async (id: string): Promise<UserProfile> => {
    const response = await axiosClient.post<UserProfile>(`/users/${id}/deactivate`);
    return response.data;
  },

  /**
   * Activate user account
   */
  activateUser: async (id: string): Promise<UserProfile> => {
    const response = await axiosClient.post<UserProfile>(`/users/${id}/activate`);
    return response.data;
  },

  /**
   * Search users
   */
  searchUsers: async (query: string, organizationId?: string): Promise<UserProfile[]> => {
    const response = await axiosClient.get<UserProfile[]>('/users/search', {
      params: { q: query, organization_id: organizationId },
    });
    return response.data;
  },

  /**
   * Change user password
   */
  changePassword: async (data: UserPasswordChange): Promise<void> => {
    await axiosClient.post('/users/me/change-password', data);
  },

  /**
   * Check if current user is admin (for showing/hiding admin features)
   */
  isCurrentUserAdmin: async (): Promise<boolean> => {
    try {
      const response = await axiosClient.get<{
        is_admin: boolean;
        user_role_id?: string;
        admin_role_id?: string;
      }>('/users/me/debug-role');
      return response.data.is_admin;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  },
};
